import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "../../store";
import { useHomeData, HeroVideo } from "../../homeData";
import { LearnSection, InstagramSection, BlogSection } from "./V1";
import { ArrowRight } from "lucide-react";

/* V4 — Tech-Spec Forward. Floating data, infographics, Apple-Watch vibe. */
export default function V4() {
  const { t } = useApp();
  const { products, blog, igPosts } = useHomeData();

  return (
    <main className="bg-white" data-testid="home-v4">
      {/* HERO with floating spec cards */}
      <section className="relative h-screen overflow-hidden">
        <HeroVideo overlay="linear-gradient(180deg, rgba(248,250,252,0.0) 0%, rgba(248,250,252,0.4) 100%)" />
        <div className="absolute inset-0 flex flex-col justify-center">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="max-w-3xl">
              <div className="overline mb-6 text-white drop-shadow">{t.hero.overline}</div>
              <h1 className="font-serif-display text-7xl md:text-9xl leading-[0.9] text-white drop-shadow-lg">{t.hero.title}</h1>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/products/efoils" className="btn-primary">{t.hero.cta1}</Link>
                <Link to="/book" className="btn-ghost !text-white !border-white">{t.hero.cta2}</Link>
              </div>
            </motion.div>
          </div>
        </div>
        {/* Floating spec chips */}
        <FloatingSpec top="18%" right="6%" delay={0.4} k="Weight" v="24 kg" />
        <FloatingSpec top="42%" right="14%" delay={0.7} k="Top Speed" v="62 km/h" />
        <FloatingSpec top="68%" right="4%" delay={1.0} k="Range" v="180 min" />
      </section>

      {/* USPs - infographic style */}
      <section className="py-28 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="overline mb-3">{t.usps.overline}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl text-slate-900">{t.usps.title}</h2>

        {/* Weight comparison infographic */}
        <div className="mt-20 bg-gradient-to-br from-slate-50 to-sky-50 rounded-3xl p-8 md:p-16 border border-slate-200">
          <div className="overline mb-3">01 — Weight</div>
          <h3 className="font-serif-display text-4xl md:text-5xl text-slate-900">{t.usps.f1d}</h3>
          <div className="mt-12 space-y-6">
            <BarRow label="SharkX E-Foil" value={24} max={40} accent />
            <BarRow label="Competitor A" value={35} max={40} />
            <BarRow label="Competitor B" value={36} max={40} />
            <BarRow label="Competitor C" value={38} max={40} />
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoTile icon="⚡" big="6s" title={t.usps.f2t} body={t.usps.f2d} />
          <InfoTile icon="🔧" big="0" title={t.usps.f3t} body={t.usps.f3d} sub="Tools required" />
        </div>
      </section>

      {/* Product showcase as data tiles */}
      <section className="py-20 px-6 md:px-12 max-w-[1440px] mx-auto">
        <h2 className="font-serif-display text-5xl md:text-6xl mb-14 text-slate-900">{t.shop.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {products.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/product/${p.slug}`} className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-sky-400 hover:shadow-xl transition-all">
              <div className="aspect-square rounded-xl overflow-hidden bg-slate-50">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="mt-5">
                <div className="overline">{p.category}</div>
                <div className="font-serif-display text-3xl text-slate-900">{p.name}</div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  {Object.entries(p.specs || {}).slice(0, 3).map(([k, v]) => (
                    <div key={k} className="bg-slate-50 rounded-md py-2">
                      <div className="text-[9px] uppercase tracking-widest text-slate-500">{k.split("_")[0]}</div>
                      <div className="font-ui font-semibold text-slate-900 text-sm">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <div className="font-serif-display text-2xl text-sky-600">${p.base_price?.toLocaleString()}</div>
                  <ArrowRight size={16} className="text-sky-500 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <LearnSection t={t} />
      <InstagramSection posts={igPosts} />
      <BlogSection blog={blog} />
    </main>
  );
}

function FloatingSpec({ top, right, delay, k, v }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay }}
      className="hidden lg:block absolute bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl px-5 py-4 shadow-2xl"
      style={{ top, right }}
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-ui">{k}</div>
      <div className="font-serif-display text-3xl text-slate-900">{v}</div>
    </motion.div>
  );
}

function BarRow({ label, value, max, accent }) {
  const w = (value / max) * 100;
  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span className="font-ui text-slate-700">{label}</span>
        <span className={`font-ui font-bold ${accent ? "text-sky-600" : "text-slate-500"}`}>{value} kg</span>
      </div>
      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${w}%` }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className={`h-full rounded-full ${accent ? "bg-gradient-to-r from-sky-400 to-cyan-400" : "bg-slate-400"}`}
        />
      </div>
    </div>
  );
}

function InfoTile({ icon, big, title, body, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-10 hover:shadow-xl hover:border-sky-300 transition-all">
      <div className="text-3xl">{icon}</div>
      <div className="font-serif-display text-7xl text-sky-600 mt-3">{big}</div>
      {sub && <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{sub}</div>}
      <h3 className="font-serif-display text-3xl mt-4 text-slate-900">{title}</h3>
      <p className="text-slate-600 mt-3">{body}</p>
    </div>
  );
}
